import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import dynamic from 'next/dynamic';
import config from 'config';
import { AdminGuard } from '@techstyle/react-admin';
import intlModule, { loadResourceBundles } from './intlModule';
import IntlProvider from './IntlProvider';
import initLocaleData from './initLocaleData';
import logger from './logger';
import DefaultCurrencyFormatProvider from './CurrencyFormatProvider';
var debug = logger.extend('intlExtension');
var IntlAdminPanel = dynamic(function () {
  return import(
  /* webpackChunkName: "AdminTools" */
  './IntlAdminPanel');
});
/**
 * The default function used for detecting locale. It uses the `tld` that was
 * detected from the request along with a TLD -> locale mapping configured in
 * `public.intl.locales` to select a locale, or `public.intl.defaultLocale` if
 * there is no match.
 */

export function defaultDetectLocale(req) {
  var locales = config.get('public.intl.locales');
  var defaultLocale = config.get('public.intl.defaultLocale');
  return locales[req.context.domain.tld] || defaultLocale;
}
export default function intlExtension() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  if (typeof options === 'function') {
    options = {
      detectLocale: options
    };
  }

  var _options = options,
      _options$detectLocale = _options.detectLocale,
      detectLocale = _options$detectLocale === void 0 ? defaultDetectLocale : _options$detectLocale,
      globalResourceBundles = _options.globalResourceBundles,
      _options$CurrencyForm = _options.CurrencyFormatProvider,
      CurrencyFormatProvider = _options$CurrencyForm === void 0 ? DefaultCurrencyFormatProvider : _options$CurrencyForm;
  initLocaleData();
  return function (registry) {
    return {
      id: 'intl',
      server: process.browser ? undefined : {
        // FIXME: `node-config` requires that all config is loaded before
        // being accessed, after which the config is frozen. It's possible to
        // disable this, but I'd rather use a better config library instead.
        init: function init() {
          require('./config');
        },
        configure: function configure(server) {
          var _require = require('./localeMiddleware'),
              localeMiddleware = _require["default"];

          var _require2 = require('./geoMiddleware'),
              geoMiddleware = _require2["default"]; // Abundance of caution: ensure backwards compatibility if someone
          // upgrades `@techstyle/react-intl` without upgrading
          // `@techstyle/next-server`. TODO: This can be removed when everyone
          // is up to date.


          var addMiddleware = function addMiddleware(name, middleware) {
            if (server.useTracked) {
              server.useTracked(name, middleware);
            } else {
              server.use(middleware);
            }
          };

          addMiddleware('geoMiddleware', geoMiddleware());
          addMiddleware('localeMiddleware', localeMiddleware(detectLocale));
        }
      },
      document: process.browser ? undefined : {
        getInitialProps: {
          enhance: function enhance(getInitialProps) {
            return /*#__PURE__*/function () {
              var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(ctx) {
                var initialProps;
                return _regeneratorRuntime.wrap(function _callee$(_context) {
                  while (1) {
                    switch (_context.prev = _context.next) {
                      case 0:
                        _context.next = 2;
                        return getInitialProps(ctx);

                      case 2:
                        initialProps = _context.sent;
                        // Add `lang` attribute to root `<html>` element.
                        initialProps.htmlProps.lang = ctx.req.context.locale; // Add locale data to a `<script>` tag that will be read by
                        // `initLocaleData` (see above) in the browser.

                        initialProps.beforeNextScript.push( /*#__PURE__*/React.createElement("script", {
                          dangerouslySetInnerHTML: {
                            __html: ctx.req.context.localeData
                          }
                        }));
                        return _context.abrupt("return", initialProps);

                      case 6:
                      case "end":
                        return _context.stop();
                    }
                  }
                }, _callee);
              }));

              return function (_x) {
                return _ref.apply(this, arguments);
              };
            }();
          }
        }
      },
      app: {
        render: function render(props, children) {
          return /*#__PURE__*/React.createElement(IntlProvider, null, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CurrencyFormatProvider, null, children), /*#__PURE__*/React.createElement(AdminGuard, null, /*#__PURE__*/React.createElement(IntlAdminPanel, null))));
        }
      },
      page: function page(_ref2) {
        var _ref2$resourceBundles = _ref2.resourceBundles,
            resourceBundles = _ref2$resourceBundles === void 0 ? [] : _ref2$resourceBundles;

        if (config.has('public.intl.globalResourceBundles')) {
          // eslint-disable-next-line no-console
          console.warn('Config option public.intl.globalResourceBundles has been deprecated, please migrate to the globalResourceBundles option on intlExtension.');
        }

        var getResourceBundles = registry.utils.getMergedArrayOption({
          globalResourceBundles: globalResourceBundles,
          resourceBundles: resourceBundles
        });
        return {
          getInitialProps: function getInitialProps(ctx) {
            return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
              var _yield$getResourceBun, _yield$getResourceBun2, resourceBundles, meta;

              return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                while (1) {
                  switch (_context2.prev = _context2.next) {
                    case 0:
                      _context2.next = 2;
                      return getResourceBundles(ctx);

                    case 2:
                      _yield$getResourceBun = _context2.sent;
                      _yield$getResourceBun2 = _slicedToArray(_yield$getResourceBun, 2);
                      resourceBundles = _yield$getResourceBun2[0];
                      meta = _yield$getResourceBun2[1];

                      if (!resourceBundles.length) {
                        _context2.next = 10;
                        break;
                      }

                      debug('Loading %s %s added by %s.', resourceBundles.length, resourceBundles.length === 1 ? 'bundle' : 'bundles', meta.sources.join(', '));
                      _context2.next = 10;
                      return ctx.store.dispatch(loadResourceBundles(resourceBundles));

                    case 10:
                    case "end":
                      return _context2.stop();
                  }
                }
              }, _callee2);
            }))();
          }
        };
      },
      redux: {
        modules: [intlModule]
      }
    };
  };
}